import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import AccessLevelService, { guest } from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import GiftsStore from 'stores/GiftsStore';
import PaymentStore from 'stores/PaymentStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import { CHAT_AVATAR_URL } from 'containers/Chat/Api';
import BalanceInfoMobile from 'components/BalanceInfoMobile/BalanceInfoMobile';
import Button from 'components/Button/Button';
import ContentCard from 'components/ContentCard/ContentCard';
import { CONTEXTUAL_GOLD_MODAL_AREAS } from 'utils/consts';

import GiftTabs from './TabContent/GiftTabs/GiftTabs';
import SendGiftForm from './TabContent/SendGiftForm/SendGiftForm';
import styles from './SendQuickGiftModal.css';


const SendQuickGift = (props) => {
  const {
    close,
    embedBuyTokens,
    embedProfileV2,
    handleSkip,
    isGoldSelected,
    isDesktop,
    profileName,
  } = props;

  const applyProfileName = profileName || ProfileStore.performerDetails.username;

  const { balance } = PaymentStore;
  const { user: { accessLevel, username, sfw } } = UserStore;
  const { showBuyTokensModal } = TippingStore;
  const [privateMessage, setPrivateMessage] = useState('');
  const [selectedGift, setSelectedGift] = useState(null);
  const { id: giftId } = selectedGift || {};
  const [anonymous, setAnonymous] = useState(false);
  const history = useHistory();
  const isGuestBilling = AccessLevelService.is(guest, accessLevel);

  useEffect(() => {
    GiftsStore.getQuickGifts(applyProfileName);
  }, [applyProfileName]);

  const handleSendGift = useCallback(() => {
    if (!giftId) {
      return;
    }

    const payload = {
      recipientName: applyProfileName,
      giftId,
      message: privateMessage,
      anonymous,
    };
    GiftsStore.sendGift(payload);
    setSelectedGift(null);
    handleSkip?.();
    close();
  }, [
    anonymous,
    close,
    giftId,
    handleSkip,
    privateMessage,
    applyProfileName,
  ]);

  const handleGuestOpenGoldOverlay = useCallback(() => {
    ModalService.showModal(ModalService.MODAL_KEYS.REGISTRATION_GOLD_MODAL, {
      history,
      onClose: ModalService.hideModal,
      showRegistrationGoldModal: true,
    });
  }, [history]);

  const upgradeToGold = useCallback(() => {
    close();

    if (!username || isGuestBilling) {
      const modalProps = {
        area: CONTEXTUAL_GOLD_MODAL_AREAS.GIFTS,
        onMembershipClick: handleGuestOpenGoldOverlay,
      };

      ModalService.showModal(
        ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
        modalProps
      );
    } else {
      ModalService.showModal(
        ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
        { area: CONTEXTUAL_GOLD_MODAL_AREAS.GIFTS }
      );
    }
  }, [close, handleGuestOpenGoldOverlay, isGuestBilling, username]);

  const onGiftClickHandler = useCallback((gift) => {
    setSelectedGift(gift);
  }, []);

  const returnToPrev = useCallback(() => {
    setSelectedGift(null);
  }, []);

  const onOpenBuyTokensModal = useCallback(() => {
    if (embedProfileV2) {
      embedBuyTokens();
    } else {
      showBuyTokensModal();
    }
    close();
  }, [close, embedBuyTokens, embedProfileV2, showBuyTokensModal]);

  const toggleCheckbox = useCallback(() => {
    setAnonymous(!anonymous);
  }, [anonymous]);

  const avatarClasses = classNames({
    [styles.profileImage]: true,
    [styles.blur]: sfw,
  });

  const hasEnoughBalance = balance >= selectedGift ?.price;

  const sendButtonElement = useMemo(() => {
    if (hasEnoughBalance) {
      return (
        <Button
          colour="orange"
          label={translate('v2profile.button.sendGift')}
          onClick={handleSendGift}
        />
      );
    }

    if (isDesktop) {
      return (
        <Button
          colour="green"
          label={translate('v2profile.buy.tokens')}
          onClick={onOpenBuyTokensModal}
        />
      );
    }

    return null;
  }, [
    handleSendGift,
    hasEnoughBalance,
    isDesktop,
    onOpenBuyTokensModal,
  ]);

  const balanceInfoElement = useMemo(() => {
    if (isDesktop || !selectedGift) {
      return null;
    }

    return (
      <BalanceInfoMobile
        areTokensNeeded={!hasEnoughBalance}
        onClick={onOpenBuyTokensModal}
      />
    );
  }, [
    hasEnoughBalance,
    isDesktop,
    selectedGift,
    onOpenBuyTokensModal,
  ]);

  return (
    <Fragment>
      <ContentCard
        className={styles.noMargin}
        contentClass={styles.SendGiftModal}
        footer={
          selectedGift && (
            <div className={styles.footerActions}>
              <span>
                <Button
                  className={styles.backButton}
                  colour="white"
                  label={translate('v2profile.gifts.back')}
                  onClick={returnToPrev}
                />
              </span>
              <span>
                {isDesktop && (
                  <Button
                    className={styles.footerCancelBtn}
                    colour="white"
                    label={translate('v2profile.gifts.cancel')}
                    onClick={close}
                  />
                )}
                {sendButtonElement}
              </span>
            </div>
          )
        }
        footerClass={styles.footerSendGiftModal}
      >
        <h4 className={styles.header}>
          <img
            alt="performer"
            className={avatarClasses}
            src={CHAT_AVATAR_URL.replace('{username}', applyProfileName)}
          />
          {translate('v2profile.send.gift.to', { 0: `'${applyProfileName}'` })}
        </h4>
        {!selectedGift && (
          <GiftTabs
            isDesktop={isDesktop}
            isGoldSelected={isGoldSelected}
            upgradeToGold={upgradeToGold}
            onGiftClickHandler={onGiftClickHandler}
          />
        )}
        {selectedGift && (
          <SendGiftForm
            anonymous={anonymous}
            close={close}
            gift={selectedGift}
            handleSend={handleSendGift}
            insufficientBalance={balance < selectedGift.price}
            isDesktop={isDesktop}
            isGuestBilling={isGuestBilling}
            privateMessage={privateMessage}
            setPrivateMessage={setPrivateMessage}
            toggleCheckbox={toggleCheckbox}
          />
        )}
      </ContentCard>
      {balanceInfoElement}
    </Fragment>
  );
};

SendQuickGift.propTypes = {
  close: PropTypes.func.isRequired,
  embedBuyTokens: PropTypes.func,
  embedProfileV2: PropTypes.bool,
  handleSkip: PropTypes.func,
  isDesktop: PropTypes.bool,
  isGoldSelected: PropTypes.bool,
  profileName: PropTypes.string,
};

SendQuickGift.defaultProps = {
  handleSkip: () => undefined,
  isDesktop: false,
  isGoldSelected: undefined,
  embedBuyTokens: () => undefined,
  embedProfileV2: false,
  profileName: undefined,
};

export default observer(SendQuickGift);
